// Sections de la candidature côté candidat (/moi/candidatures/:applicationId) :
// décision et échanges, corrections demandées, garanties, consentements, identité et activité.
import { useState } from "react";
import { post, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Fil, Input, Loading, MoneyInput, Notice, Select, StatusChip, Textarea, fieldError, type CheckStatus } from "@core/ui";
import { CheckLine, ChoiceGroup, OfficialLink, OutcomeChip, docUrl, useAction, type Check } from "./shared";
import { COMMITMENT_LABELS, COMMITMENT_STEPS, VISALE_LABELS, VISALE_STEPS, steps } from "./GuaranteesTab";
import { EMPLOYER_STATUS, EmployerAnswers, type EmployerRequest } from "./VerificationsTab";
import { CORRECTION_STATUS } from "./CorrectionsTab";

const prefix = (applicationId: string) => `/api/v1/applications/${applicationId}`;

// ---------------------------------------------------------------------------
// Décision et échanges

type CandidateDecisionView = {
  application: { id: string; status: string; campaign_title: string };
  me: { role: string };
  decision: { id: string; outcome: string; label: string; reasons: string[]; decided_at: string } | null;
  contestations: { id: string; kind: string; message: string; status: string; response: string | null; responded_at: string | null; created_at: string; decision_changed: boolean }[];
};

export function CandidateDecision({ applicationId }: { applicationId: string }) {
  const q = useGet<CandidateDecisionView>(`${prefix(applicationId)}/decision`);
  const action = useAction([prefix(applicationId)]);
  const [kind, setKind] = useState<"" | "explication" | "contestation">("");
  const [message, setMessage] = useState("");
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  return (
    <Card title="Décision et échanges">
      {v.decision ? (
        <div className="stack-sm">
          <p className="row">
            <OutcomeChip outcome={v.decision.outcome} />
            <span className="small muted">Décision du bailleur le {dateTime(v.decision.decided_at)}</span>
          </p>
          {v.decision.reasons.length ? (
            <>
              <p className="small muted">Motifs indiqués par le bailleur :</p>
              <ul>
                {v.decision.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </>
          ) : null}
          <p className="small muted">Cette décision a été prise par le bailleur, pas par un traitement automatique. Vous pouvez demander des explications ou la contester : le bailleur vous répondra ici.</p>
        </div>
      ) : (
        <Empty title="Pas encore de décision">Le bailleur décide après avoir examiné votre dossier. Aucun traitement automatique ne décide à sa place.</Empty>
      )}
      {v.decision ? (
        <form
          className="stack decision-sous-formulaire"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!kind) return;
            const ok = await action.run("envoyer", () => post(`${prefix(applicationId)}/contestations`, { kind, message }), "Votre demande a été transmise au bailleur.");
            if (ok) {
              setKind("");
              setMessage("");
            }
          }}
        >
          <ChoiceGroup
            legend="Votre demande"
            name="decision-demande"
            value={kind}
            onChange={setKind}
            options={[
              { value: "explication", label: "Demander des explications", help: "Comprendre les motifs de la décision." },
              { value: "contestation", label: "Contester la décision", help: "Signaler une erreur ou un élément non pris en compte." },
            ]}
          />
          <Textarea label="Votre message" required value={message} error={fieldError(action.error, "message")} onChange={(e) => setMessage(e.target.value)} help="Précisez les faits ; vous pouvez aussi déposer une pièce corrigée depuis les corrections demandées." />
          <div className="form-actions">
            <Button type="submit" busy={action.busy === "envoyer"} disabled={!kind}>
              Envoyer au bailleur
            </Button>
          </div>
        </form>
      ) : null}
      {v.contestations.length ? (
        <div className="stack">
          <h3>Vos demandes</h3>
          {v.contestations.map((c) => (
            <article key={c.id} className="decision-echange">
              <header className="row-between">
                <strong>{c.kind === "contestation" ? "Contestation" : "Demande d’explication"}</strong>
                <StatusChip status={c.status === "ouverte" ? "a_confirmer" : "conforme"} label={c.status === "ouverte" ? "En attente de réponse" : "Répondu"} />
              </header>
              <p className="small muted">Envoyée le {dateTime(c.created_at)}</p>
              <p className="message-body">{c.message}</p>
              {c.response ? (
                <div className="decision-reponse">
                  <p className="small muted">
                    Réponse du bailleur le {dateTime(c.responded_at)}
                    {c.decision_changed ? " · la décision a été modifiée" : ""}
                  </p>
                  <p className="message-body">{c.response}</p>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Corrections demandées

type CandidateCorrection = {
  id: string;
  applicant_name: string | null;
  category_label: string | null;
  title: string;
  message: string;
  status: string;
  candidate_reply: string | null;
  candidate_reply_at: string | null;
  resolution: string | null;
  resolved_at: string | null;
  created_at: string;
};

export function CandidateCorrections({ applicationId }: { applicationId: string }) {
  const q = useGet<{ items: CandidateCorrection[] }>(`${prefix(applicationId)}/corrections`);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const items = q.data!.items;
  const open = items.filter((i) => i.status === "ouverte");
  return (
    <Card title="Corrections demandées">
      <p className="muted">Votre dossier est vérifié avec les pièces que vous fournissez. Quand un point reste à confirmer, le bailleur peut vous demander une pièce ou une précision : vous pouvez corriger.</p>
      {open.length ? (
        <div className="stack">
          {open.map((c) => (
            <CandidateCorrectionItem key={c.id} c={c} applicationId={applicationId} />
          ))}
        </div>
      ) : (
        <Empty title="Aucune correction demandée">Rien à compléter pour l’instant.</Empty>
      )}
      {items.length > open.length ? (
        <details>
          <summary>Demandes closes ({items.length - open.length})</summary>
          <ul className="list-plain stack-sm">
            {items
              .filter((i) => i.status !== "ouverte")
              .map((c) => (
                <li key={c.id}>
                  <StatusChip status={CORRECTION_STATUS[c.status]?.status ?? "info"} label={CORRECTION_STATUS[c.status]?.label ?? c.status} /> {c.title}
                  <span className="small muted"> · {dateTime(c.resolved_at)}</span>
                </li>
              ))}
          </ul>
        </details>
      ) : null}
    </Card>
  );
}

function CandidateCorrectionItem({ c, applicationId }: { c: CandidateCorrection; applicationId: string }) {
  const [reply, setReply] = useState("");
  const action = useAction([prefix(applicationId)]);
  return (
    <article className="decision-echange">
      <header className="row-between">
        <strong>{c.title}</strong>
        <StatusChip status="a_confirmer" label="À compléter" />
      </header>
      <p className="small muted">
        Demandée le {dateTime(c.created_at)}
        {c.applicant_name ? ` · concerne ${c.applicant_name}` : ""}
        {c.category_label ? ` · pièce attendue : ${c.category_label}` : ""}
      </p>
      <p className="message-body">{c.message}</p>
      <FileDrop
        label="Déposer la pièce demandée"
        busy={action.busy === "piece"}
        onFiles={(files) => {
          const fd = new FormData();
          fd.append("file", files[0]);
          action.run("piece", () => upload(`${prefix(applicationId)}/corrections/${c.id}/piece`, fd), "Pièce déposée : le bailleur en est informé.");
        }}
      />
      {c.candidate_reply ? (
        <p className="small">
          Votre réponse du {dateTime(c.candidate_reply_at)} : {c.candidate_reply}
        </p>
      ) : (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await action.run("repondre", () => post(`${prefix(applicationId)}/corrections/${c.id}/reply`, { message: reply }), "Réponse envoyée au bailleur.");
            if (ok) setReply("");
          }}
        >
          <Textarea label="Ou répondre par écrit" help="Par exemple : expliquer un écart, indiquer que vous ne pouvez pas fournir la pièce." value={reply} error={fieldError(action.error, "message")} onChange={(e) => setReply(e.target.value)} />
          <div className="form-actions">
            <Button type="submit" small variant="secondaire" busy={action.busy === "repondre"}>
              Envoyer ma réponse
            </Button>
          </div>
        </form>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Mes garanties

type CandidateGuaranteesView = {
  me: { id: string; role: string };
  commitments: {
    id: string;
    guarantor_name: string;
    status: string;
    amount_cents: number;
    duration_months: number | null;
    engagement_type: string;
    act_document_id: string | null;
    confirmed_at: string | null;
    signed_on: string | null;
    is_mine: boolean;
    can_confirm: boolean;
    mention: string | null;
  }[];
  visale: { id: string; applicant_name: string | null; visa_number: string; valid_until: string; max_rent_cents: number; status: string; verified_on: string | null; contract_signed_on: string | null; activated_on: string | null }[];
  can_declare_visale: boolean;
  rent_total_cents: number;
};

export function CandidateGuarantees({ applicationId }: { applicationId: string }) {
  const q = useGet<CandidateGuaranteesView>(`${prefix(applicationId)}/guarantees`);
  const action = useAction([prefix(applicationId)]);
  const [visa, setVisa] = useState({ visa_number: "", valid_until: "", max_rent_cents: null as number | null });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  return (
    <Card title="Mes garanties">
      {v.commitments.length ? (
        <div className="stack">
          {v.commitments.map((c) => (
            <article key={c.id} className="decision-echange">
              <header className="row-between">
                <strong>{c.is_mine ? "Votre engagement de caution" : `Engagement de ${c.guarantor_name}`}</strong>
                <StatusChip status={c.status === "signe" ? "conforme" : "a_confirmer"} label={COMMITMENT_LABELS[c.status] ?? c.status} />
              </header>
              <Fil steps={steps(COMMITMENT_STEPS, COMMITMENT_LABELS, c.status)} />
              <p className="small">
                Engagement {c.engagement_type === "solidaire" ? "solidaire" : "simple"} dans la limite de <strong>{money(c.amount_cents)}</strong>, {c.duration_months ? `pour ${c.duration_months} mois` : "pour une durée indéterminée"}.
                {c.act_document_id ? (
                  <>
                    {" "}
                    <a href={docUrl(c.act_document_id)} target="_blank" rel="noreferrer">
                      Lire l’acte de cautionnement (PDF)
                    </a>
                  </>
                ) : null}
              </p>
              {c.can_confirm && c.mention ? <GuarantorConfirm applicationId={applicationId} id={c.id} mention={c.mention} amount={c.amount_cents} /> : null}
              {!c.is_mine && !c.confirmed_at ? <p className="small muted">Seul votre garant peut confirmer son engagement ; il reçoit une invitation personnelle.</p> : null}
            </article>
          ))}
        </div>
      ) : (
        <p className="muted">Aucun acte de cautionnement préparé pour ce dossier.</p>
      )}
      {v.can_declare_visale ? (
        <div className="stack">
          <h3>Visa Visale</h3>
          <Notice tone="attention">Un visa Visale n’est pas une garantie activée : le bailleur le vérifie, puis obtient le contrat de cautionnement Visale avant la signature du bail.</Notice>
          {v.visale.map((x) => (
            <article key={x.id} className="decision-echange">
              <header className="row-between">
                <strong>Visa {x.visa_number}</strong>
                <StatusChip status={x.status === "actif" ? "conforme" : "a_confirmer"} label={VISALE_LABELS[x.status] ?? x.status} />
              </header>
              <Fil steps={steps(VISALE_STEPS, VISALE_LABELS, x.status)} />
              <p className="small muted">
                Valable jusqu’au {date(x.valid_until)} · loyer maximal {money(x.max_rent_cents)} (loyer charges comprises du logement : {money(v.rent_total_cents)})
              </p>
            </article>
          ))}
          {!v.visale.length ? (
            <form
              className="stack-sm"
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await action.run("visa", () => post(`${prefix(applicationId)}/visale`, visa), "Visa transmis au bailleur.");
                if (ok) setVisa({ visa_number: "", valid_until: "", max_rent_cents: null });
              }}
            >
              <div className="form-row">
                <Input label="Numéro de visa" required value={visa.visa_number} error={fieldError(action.error, "visa_number")} onChange={(e) => setVisa({ ...visa, visa_number: e.target.value })} />
                <Input label="Valable jusqu’au" type="date" required value={visa.valid_until} error={fieldError(action.error, "valid_until")} onChange={(e) => setVisa({ ...visa, valid_until: e.target.value })} />
                <MoneyInput label="Loyer maximal indiqué sur le visa" required cents={visa.max_rent_cents} error={fieldError(action.error, "max_rent_cents")} onChange={(c) => setVisa({ ...visa, max_rent_cents: c })} />
              </div>
              <div className="form-actions">
                <Button type="submit" variant="secondaire" busy={action.busy === "visa"}>
                  Transmettre mon visa
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function GuarantorConfirm({ applicationId, id, mention, amount }: { applicationId: string; id: string; mention: string; amount: number }) {
  const [form, setForm] = useState({ full_name: "", mention: "", accept: false });
  const action = useAction([prefix(applicationId)]);
  return (
    <form
      className="stack-sm decision-sous-formulaire"
      onSubmit={(e) => {
        e.preventDefault();
        action.run("confirmer", () => post(`${prefix(applicationId)}/guarantor-commitments/${id}/confirm`, form), "Votre engagement est confirmé. L’acte vous sera proposé à la signature.");
      }}
    >
      <p className="small">Lisez l’acte, puis recopiez vous-même la mention ci-dessous (montant maximal : {money(amount)}).</p>
      <blockquote className="decision-mention">{mention}</blockquote>
      <Input label="Vos nom et prénom" required value={form.full_name} error={fieldError(action.error, "full_name")} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
      <Textarea label="Mention recopiée par vous-même" required value={form.mention} error={fieldError(action.error, "mention")} onChange={(e) => setForm({ ...form, mention: e.target.value })} />
      <Checkbox label="J’ai lu l’acte et je m’engage en qualité de caution dans ces conditions." checked={form.accept} onChange={(c) => setForm({ ...form, accept: c })} />
      {fieldError(action.error, "accept") ? (
        <p className="bv-champ-aide decision-erreur" role="alert">
          {fieldError(action.error, "accept")}
        </p>
      ) : null}
      <div className="form-actions">
        <Button type="submit" busy={action.busy === "confirmer"}>
          Confirmer mon engagement
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Consentements (confirmation par l'employeur) et parcours guidés

type CandidateVerificationsView = {
  me: { id: string; role: string; name: string };
  org_name: string;
  employer_confirmations: (EmployerRequest & { consent_text: string })[];
  social_rights: { id: string; status: string; document_id: string | null; period_start: string | null; period_end: string | null; verified_on: string | null }[];
  identity_checks: { id: string; method: string; status: string; document_id: string | null; foreign_document: boolean }[];
  checks: Check[];
  links: { mes_droits_sociaux: string; mes_droits_sociaux_aide: string; france_identite: string; france_identite_aide: string };
  alternatives: string;
  document_kinds: { value: string; label: string }[];
};

export function CandidateConsents({ applicationId }: { applicationId: string }) {
  const q = useGet<CandidateVerificationsView>(`${prefix(applicationId)}/external-verifications`);
  const action = useAction([prefix(applicationId)]);
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_phone: "", consent: false });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  const active = v.employer_confirmations.some((e) => ["attente_consentement", "prete", "envoyee"].includes(e.status));
  return (
    <Card title="Consentements">
      <p className="muted">
        Le bailleur peut, avec votre accord, demander à votre employeur une confirmation récente de votre emploi. Seules quatre informations sont demandées ; votre dossier n’est jamais transmis. Vous pouvez refuser : ce n’est pas un motif défavorable et un autre justificatif convient.
      </p>
      {v.employer_confirmations.length ? (
        <div className="stack">
          {v.employer_confirmations.map((e) => {
            const st = EMPLOYER_STATUS[e.status] ?? { label: e.status, status: "info" as CheckStatus };
            return (
              <article key={e.id} className="decision-echange">
                <header className="row-between">
                  <strong>Confirmation par {e.contact_name}</strong>
                  <StatusChip status={st.status} label={st.label} />
                </header>
                <p className="small muted">
                  Contact : {e.contact_email} · proposé par {e.contact_source === "candidat" ? "vous" : "le bailleur"}
                  {e.consent_at ? ` · accord donné le ${dateTime(e.consent_at)}` : ""}
                  {e.refused_at ? ` · refusée le ${dateTime(e.refused_at)}` : ""}
                </p>
                {e.status === "attente_consentement" ? (
                  <div className="stack-sm">
                    <blockquote className="decision-mention">{e.consent_text}</blockquote>
                    <div className="form-actions">
                      <Button small busy={action.busy === `ok-${e.id}`} onClick={() => action.run(`ok-${e.id}`, () => post(`${prefix(applicationId)}/employer-confirmations/${e.id}/consent`, { accept: true }), "Accord enregistré.")}>
                        Je donne mon accord
                      </Button>
                      <Button small variant="secondaire" busy={action.busy === `non-${e.id}`} onClick={() => action.run(`non-${e.id}`, () => post(`${prefix(applicationId)}/employer-confirmations/${e.id}/consent`, { accept: false }), "Refus enregistré.")}>
                        Je refuse
                      </Button>
                    </div>
                  </div>
                ) : null}
                {e.status === "refusee_candidat" ? <p className="small">{v.alternatives}</p> : null}
                <EmployerAnswers r={e} />
              </article>
            );
          })}
        </div>
      ) : null}
      {!active ? (
        <form
          className="stack-sm decision-sous-formulaire"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await action.run("proposer", () => post(`${prefix(applicationId)}/employer-confirmations`, { ...form, contact_phone: form.contact_phone || null }), "Contact transmis au bailleur avec votre accord.");
            if (ok) setForm({ contact_name: "", contact_email: "", contact_phone: "", consent: false });
          }}
        >
          <h3>Proposer vous-même un contact RH</h3>
          <div className="form-row">
            <Input label="Interlocuteur RH" required value={form.contact_name} error={fieldError(action.error, "contact_name")} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            <Input label="Adresse e-mail" type="email" required value={form.contact_email} error={fieldError(action.error, "contact_email")} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            <Input label="Téléphone (facultatif)" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
          <Checkbox
            label="J’autorise le bailleur à demander à ce contact de confirmer uniquement : si je suis en poste, le type de mon contrat, la fin de ma période d’essai et l’existence d’un préavis."
            checked={form.consent}
            onChange={(c) => setForm({ ...form, consent: c })}
          />
          {fieldError(action.error, "consent") ? (
            <p className="bv-champ-aide decision-erreur" role="alert">
              {fieldError(action.error, "consent")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button type="submit" variant="secondaire" busy={action.busy === "proposer"}>
              Transmettre ce contact
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

export function CandidateGuidedChecks({ applicationId }: { applicationId: string }) {
  const q = useGet<CandidateVerificationsView>(`${prefix(applicationId)}/external-verifications`);
  const action = useAction([prefix(applicationId)]);
  const [period, setPeriod] = useState({ period_start: "", period_end: "" });
  const [identity, setIdentity] = useState({ method: "france_identite", document_kind: "justificatif_france_identite", foreign_document: false });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  const social = v.social_rights[0];
  const kinds = v.document_kinds.filter((k) => (identity.method === "france_identite" ? k.value === "justificatif_france_identite" : k.value !== "justificatif_france_identite"));
  return (
    <Card title="Identité et activité">
      <div className="stack">
        <section className="stack-sm">
          <h3>Attestation Mes droits sociaux</h3>
          <p className="small">
            1. Connectez-vous sur <OfficialLink href={v.links.mes_droits_sociaux}>mesdroitssociaux.gouv.fr</OfficialLink> avec vos propres identifiants. Bailivo ne vous les demande jamais. 2. Téléchargez l’attestation qui retrace votre activité. 3. Déposez-la ci-dessous.{" "}
            <OfficialLink href={v.links.mes_droits_sociaux_aide}>Guide d’aide</OfficialLink>
          </p>
          {social ? (
            <p className="small muted">
              {social.status === "attente_depot" ? "Le bailleur vous propose ce parcours." : social.status === "deposee" ? "Attestation déposée, en attente d’examen par le bailleur." : `Attestation examinée le ${date(social.verified_on)}.`}
            </p>
          ) : null}
          {!social || social.status === "attente_depot" ? (
            <>
              <div className="form-row">
                <Input label="Début de la période couverte (facultatif)" type="date" value={period.period_start} onChange={(e) => setPeriod({ ...period, period_start: e.target.value })} />
                <Input label="Fin de la période couverte (facultatif)" type="date" value={period.period_end} error={fieldError(action.error, "period_end")} onChange={(e) => setPeriod({ ...period, period_end: e.target.value })} />
              </div>
              <FileDrop
                label="Déposer l’attestation"
                busy={action.busy === "attestation"}
                onFiles={(files) => {
                  const fd = new FormData();
                  fd.append("file", files[0]);
                  if (period.period_start) fd.append("period_start", period.period_start);
                  if (period.period_end) fd.append("period_end", period.period_end);
                  action.run("attestation", () => upload(`${prefix(applicationId)}/social-rights`, fd), "Attestation déposée.");
                }}
              />
            </>
          ) : null}
          <p className="small muted">Si l’attestation n’est pas disponible pour votre situation, un autre justificatif d’activité admissible convient.</p>
        </section>

        <section className="stack-sm">
          <h3>Justificatif d’identité</h3>
          <p className="small">
            Avec l’application France Identité, vous pouvez créer un justificatif à usage unique au nom de « {v.org_name} », pour cette location.{" "}
            <OfficialLink href={v.links.france_identite_aide}>Comment faire</OfficialLink>. Sans l’application, ou avec un document étranger, déposez une copie d’une pièce d’identité admissible : c’est tout aussi valable. Aucune photo de vous ni reconnaissance faciale n’est demandée.
          </p>
          {v.identity_checks.map((i) => (
            <p key={i.id} className="small muted">
              {i.method === "france_identite" ? "Justificatif France Identité" : "Pièce d’identité"} : {i.status === "attente_depot" ? "proposé par le bailleur" : i.status === "deposee" ? "déposé, en attente d’examen" : "examiné par le bailleur"}
            </p>
          ))}
          <div className="form-row">
            <Select
              label="Parcours"
              value={identity.method}
              onChange={(e) =>
                setIdentity({ method: e.target.value, document_kind: e.target.value === "france_identite" ? "justificatif_france_identite" : "carte_identite", foreign_document: false })
              }
              options={[
                { value: "france_identite", label: "Justificatif à usage unique France Identité" },
                { value: "piece_classique", label: "Pièce d’identité (française ou étrangère)" },
              ]}
            />
            <Select label="Type de pièce" value={identity.document_kind} onChange={(e) => setIdentity({ ...identity, document_kind: e.target.value })} options={kinds} />
          </div>
          {identity.method === "piece_classique" ? <Checkbox label="Document étranger" checked={identity.foreign_document} onChange={(c) => setIdentity({ ...identity, foreign_document: c })} /> : null}
          <FileDrop
            label="Déposer le justificatif"
            busy={action.busy === "identite"}
            onFiles={(files) => {
              const fd = new FormData();
              fd.append("file", files[0]);
              fd.append("method", identity.method);
              fd.append("document_kind", identity.document_kind);
              fd.append("foreign_document", identity.foreign_document ? "true" : "false");
              action.run("identite", () => upload(`${prefix(applicationId)}/identity`, fd), "Justificatif déposé.");
            }}
          />
          <ErrorBox error={action.error} />
        </section>

        {v.checks.length ? (
          <section className="stack-sm">
            <h3>Constats vous concernant</h3>
            {v.checks.map((c) => (
              <CheckLine key={c.id} check={c} />
            ))}
          </section>
        ) : null}
      </div>
    </Card>
  );
}
